/**
 * AuthBrandPanel — the left half of every auth screen.
 * @file src/web/features/auth/components/AuthBrandPanel.tsx
 *
 * @llm-rule WHEN: Building any signed-out page (sign in, forgot, reset)
 * @llm-rule NOTE: One panel for the whole auth flow, so moving between those
 *   screens never redraws the brand side — only the form beside it changes.
 * @llm-rule AVOID: Putting anything interactive in here. It is the poster; the
 *   form is the product.
 */

import { useEffect, useState } from 'react';
import { MapPin, ScanFace, ShieldCheck } from 'lucide-react';
import { PRODUCT } from '../../../shared/brand';
import { FaceMesh } from './FaceMesh';

/**
 * A running clock. On a product whose entire job is what time you arrived,
 * a live wall clock is the most honest thing the poster can show.
 */
function LiveClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = now.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const seconds = now.toLocaleTimeString(undefined, { second: '2-digit' }).padStart(2, '0');

  return (
    <div className="flex items-baseline gap-1.5 tnum">
      <span className="text-[34px] font-semibold leading-none tracking-tight text-white">
        {time}
      </span>
      <span className="text-[15px] font-medium leading-none text-white/45">:{seconds}</span>
    </div>
  );
}

const PROOF = [
  { icon: ScanFace, text: 'Face verified on device' },
  { icon: MapPin, text: 'Tied to where it happened' },
  { icon: ShieldCheck, text: 'One enrolment photo, matched every day' },
];

export function AuthBrandPanel() {
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="aurora relative hidden lg:flex lg:w-[46%] lg:max-w-[620px] lg:flex-col lg:justify-between lg:p-12">
      <div className="aurora-grid" />

      {/* Wordmark */}
      <div className="relative z-10 anim-rise">
        <div className="flex items-center gap-3">
          <div className="grid size-9 place-items-center rounded-[11px] bg-white/10 ring-1 ring-inset ring-white/25 backdrop-blur-sm">
            <ScanFace className="size-[19px] text-white" strokeWidth={1.8} />
          </div>
          <div className="leading-tight">
            <div className="text-[17px] font-semibold tracking-tight text-white">
              {PRODUCT.name}
            </div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-white/45">
              by Voilacode
            </div>
          </div>
        </div>
      </div>

      {/* Artwork */}
      <div className="relative z-10 flex flex-1 items-center justify-center py-8">
        <div className="relative w-full max-w-[300px]">
          <FaceMesh className="w-full drop-shadow-[0_0_28px_oklch(0.7_0.14_260/0.35)]" />
        </div>
      </div>

      {/* Clock, tagline, proof */}
      <div className="relative z-10 anim-rise" style={{ ['--d' as string]: '120ms' }}>
        <LiveClock />
        <div className="mt-1 text-[12.5px] text-white/45">{today}</div>

        <h2 className="mt-7 max-w-[19ch] text-[30px] font-semibold leading-[1.12] tracking-[-0.02em] text-white">
          {PRODUCT.tagline}
        </h2>
        <p className="mt-3 max-w-[42ch] text-[13.5px] leading-relaxed text-white/55">
          {PRODUCT.subtitle}
        </p>

        <ul className="mt-7 flex flex-col gap-2.5">
          {PROOF.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-2.5 text-[12.5px] text-white/70">
              <Icon className="size-[15px] flex-none text-white/40" strokeWidth={1.7} />
              {text}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
